/**
 * Zod input schemas: expenses. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { FxRateSchema, Iso3166CodeSchema, Iso4217CodeSchema, IsoDateStringSchema, MoneyAmountSchema, MonthSchema, ShortTextSchema, YearSchema } from './primitives.js';
// ===========================================================================
// STATEMENT — operations
// ===========================================================================
/**
 * Create a new draft statement. payoutCurrency is NOT in this schema — the
 * server snapshots it from the user's profile at creation time (per spec).
 */
export const StatementCreateSchema = z.object({
    name: ShortTextSchema,
    month: MonthSchema,
    year: YearSchema,
});
/** Edit a draft's metadata (only allowed while status === 'new'). */
export const StatementMetaEditSchema = z.object({
    statementId: z.string().min(1),
    name: ShortTextSchema,
});
/** Submit a draft. Confirmation flag is enforced at the server too. */
export const StatementSubmitSchema = z.object({
    statementId: z.string().min(1),
    confirmAccurate: z.literal(true, {
        errorMap: () => ({
            message: 'You must confirm entries are accurate before submitting',
        }),
    }),
});
/** Recall a submitted statement back to draft. */
export const StatementRecallSchema = z.object({
    statementId: z.string().min(1),
});
/** Reject a statement (accounting or 2nd approver). Reason is mandatory. */
export const StatementRejectSchema = z.object({
    statementId: z.string().min(1),
    rejectionReason: ShortTextSchema,
});
/** Forward to 2nd approver (accounting only). */
export const StatementForwardSchema = z.object({
    statementId: z.string().min(1),
    approverEmail: z.string().email(),
    comment: ShortTextSchema,
});
/** Approve a statement. */
export const StatementApproveSchema = z.object({
    statementId: z.string().min(1),
});
/** Admin reassignment — same as forward, plus an explicit reason for audit. */
export const StatementReassignSchema = z.object({
    statementId: z.string().min(1),
    newApproverEmail: z.string().email(),
    reason: ShortTextSchema,
});
// ===========================================================================
// LINE ITEMS
// ===========================================================================
/**
 * Add or edit an expense line item. The receipt file uploads separately to
 * Firebase Storage; this schema only carries the metadata.
 */
export const ExpenseLineItemInputSchema = z
    .object({
    statementId: z.string().min(1),
    /**
     * Optional client-supplied document ID for the new line item.
     * If provided, the server uses it as the Firestore doc ID instead of
     * generating one. This allows the client to upload the receipt to a
     * stable storage path (`receipts/{statementId}/{lineItemId}/...`)
     * BEFORE the line item is created, so the path field on the line item
     * actually corresponds to its own ID.
     *
     * Format: 20 alphanumeric chars (matches Firestore auto-id pattern).
     * Falsy → server auto-generates.
     */
    clientLineItemId: z
        .string()
        .regex(/^[A-Za-z0-9_-]{15,30}$/, 'Invalid client-generated ID format')
        .optional(),
    /** ISO YYYY-MM-DD. Server converts to Timestamp. */
    expenseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    // Plain string: the type list is admin-configurable (config.expenseTypes).
    // Membership is validated server-side in the add/edit callables.
    expenseType: z.string().trim().min(1, 'Expense type is required'),
    receiptCurrency: Iso4217CodeSchema,
    receiptAmount: MoneyAmountSchema,
    /**
     * Expense purpose — labeled "Expense purpose" in the UI but kept as
     * `businessReason` in Firestore for backwards compatibility with existing
     * line items. Required on every expense (per spec change).
     */
    businessReason: z
        .string()
        .trim()
        .min(3, 'Expense purpose is required (minimum 3 characters)')
        .max(2000),
    /** Storage path of the uploaded receipt, if any. Null is allowed pre-submit. */
    receiptStoragePath: z.string().min(1).nullable(),
    receiptHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/, 'Must be a 64-char hex SHA-256 digest')
        .nullable(),
});
export const ExpenseLineItemEditSchema = ExpenseLineItemInputSchema.and(z.object({ lineItemId: z.string().min(1) }));
/**
 * Travel allowance input. The server computes `dayBreakdown` from country
 * rates + start/end times; the client doesn't pass it.
 */
export const TravelAllowanceInputSchema = z
    .object({
    statementId: z.string().min(1),
    countryCode: Iso3166CodeSchema,
    startDateTime: z.coerce.date(),
    endDateTime: z.coerce.date(),
    /**
     * Wall-clock hour/minute the user typed in the form, BEFORE the browser's
     * local-TZ → UTC conversion. Used by the day-breakdown logic to apply
     * the early-start/late-end full-day bump rules using the actual hour the
     * user meant (rather than the UTC-shifted hour after Date round-trip).
     * Optional for backwards-compat — pre-bump-feature clients won't send it,
     * and the breakdown logic falls back to UTC hours.
     */
    startLocalHour: z.number().int().min(0).max(23).optional(),
    startLocalMinute: z.number().int().min(0).max(59).optional(),
    endLocalHour: z.number().int().min(0).max(23).optional(),
    endLocalMinute: z.number().int().min(0).max(59).optional(),
})
    .superRefine((data, ctx) => {
    if (data.endDateTime.getTime() <= data.startDateTime.getTime()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endDateTime'],
            message: 'End must be after start',
        });
    }
    // Sanity ceiling: 60 days. Real-world business trips longer than that
    // need a different process and probably a manual conversation with HR.
    const days = (data.endDateTime.getTime() - data.startDateTime.getTime()) / 86_400_000;
    if (days > 60) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endDateTime'],
            message: 'Trip cannot exceed 60 days',
        });
    }
});
export const TravelAllowanceEditSchema = TravelAllowanceInputSchema.and(z.object({ lineItemId: z.string().min(1) }));
// ===========================================================================
// MANUAL FX RATE (accounting-only)
// ===========================================================================
/**
 * When `fxRateStatus === 'pending_admin'`, accounting must enter a rate
 * before approving. Recorded as `fxRateSource: 'manual'` with audit log entry.
 */
export const ManualFxRateSchema = z.object({
    statementId: z.string().min(1),
    lineItemId: z.string().min(1),
    fxRate: FxRateSchema,
    fxRateDate: IsoDateStringSchema,
    reason: ShortTextSchema,
});
// ===========================================================================
// COMMENTS (internal, accounting+admins only)
// ===========================================================================
export const CommentCreateSchema = z.object({
    statementId: z.string().min(1),
    body: ShortTextSchema,
});
/** Director/CEO team view of expense statements (read-only, per year). */
export const ExpenseTeamStatementsSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    /** Org-wide viewers (CEO/COO, Admin) narrowing to their own reporting line. */
    ownLineOnly: z.boolean().nullish().transform((v) => v === true),
});
/** Read-only detail of one team statement (line items, no receipts). */
export const ExpenseTeamStatementDetailSchema = z.object({
    statementId: z.string().trim().min(1).max(80),
});
//# sourceMappingURL=expenses.js.map